import { CrudFilters, CrudOperators, DataProvider } from '@pankod/refine-core'
import { generateSort } from '@pankod/refine-simple-rest'
import { AxiosInstance, AxiosResponse } from 'axios'
import { stringify } from 'query-string'

import { API_URL } from '@/lib/constants'

const mapOperator = (operator: CrudOperators): string => {
  switch (operator) {
    case 'eq':
      return ''
    default:
      throw new Error(`Operator ${operator} is not supported`)
  }
}

const generateFilter = (filters?: CrudFilters) => {
  const queryFilters: { [key: string]: string } = {}

  if (filters) {
    let index = 0
    filters.map((filter) => {
      if (filter.operator === 'or' || filter.operator === 'and') {
        throw new Error(`Operator: ${filter.operator} is not supported`)
      }

      if ('field' in filter) {
        queryFilters[`filters[${index}][field]`] = filter.field
        queryFilters[`filters[${index}][value]`] = filter.value
        queryFilters[`filters[${index}][operator]`] = mapOperator(filter.operator)
        index += 1
      }
    })
  }

  return queryFilters
}

export const dataProvider = (httpClient: AxiosInstance): DataProvider => {
  return {
    getList: async ({ resource, pagination = { current: 1, pageSize: 10 }, filters, sort }) => {
      const url = `${API_URL}/api/get/${resource}`

      const { current = 1, pageSize = 10 } = pagination ?? {}

      const queryFilters = generateFilter(filters)

      const query: {
        offset?: number
        limit?: number
        sort?: string
        order?: string
      } = {
        offset: (current - 1) * pageSize,
        limit: pageSize,
      }

      const generatedSort = generateSort(sort)
      if (generatedSort) {
        const { _sort, _order } = generatedSort
        query.sort = _sort.join(',')
        query.order = _order.join(',')
      }

      const { data, headers } = await httpClient.get(`${url}?${stringify(query)}&${stringify(queryFilters)}`)

      const total = +headers['x-total-count']

      return {
        data,
        total,
      }
    },

    getMany: async ({ resource, ids }) => {
      const { data } = await httpClient.get(`${API_URL}/api/get/${resource}?${stringify({ id: ids })}`)

      return {
        data,
      }
    },

    create: async ({ resource, variables }) => {
      const url = `${API_URL}/api/post/${resource}`

      const { data } = await httpClient.post(url, variables)

      return {
        data,
      }
    },

    update: async ({ resource, id, variables }) => {
      const url = `${API_URL}/api/patch/${resource}/${id}`

      const { data } = await httpClient.patch(url, variables)

      return {
        data,
      }
    },

    getOne: async ({ resource, id }) => {
      const url = `${API_URL}/api/get/${resource}/${id}`

      const { data } = await httpClient.get(url)

      return {
        data,
      }
    },

    deleteOne: async ({ resource, id, variables }) => {
      const url = `${API_URL}/api/delete/${resource}/${id}`

      const { data } = await httpClient.delete(url, {
        data: variables,
      })

      return {
        data,
      }
    },

    getApiUrl: () => {
      return API_URL
    },

    custom: async ({ url, method, payload = undefined }) => {
      const requestUrl = `${API_URL}/api/${method}/${url}`

      let axiosResponse: AxiosResponse

      switch (method) {
        case 'put':
        case 'post':
        case 'patch':
          axiosResponse = await httpClient[method](requestUrl, payload)
          break
        case 'delete':
          axiosResponse = await httpClient.delete(requestUrl, {
            data: payload,
          })
          break
        default:
          axiosResponse = await httpClient.get(requestUrl)
          break
      }

      return axiosResponse.data
    },
  }
}
